import React, { useEffect, useState } from 'react';
import {
    CardContent,
    Button,
    Grid,
    Box,
    Typography,
    Divider,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { EnchancedModal } from 'src/components/Modals';
import { GridItem } from 'src/components/Search/components';
import useStore from 'src/hooks/useStore';
import useMake from 'src/hooks/useMake';
import useVehicle from 'src/hooks/useVehicle';
import useUser from 'src/hooks/useUser';
import useAuth from 'src/hooks/useAuth';
import { mapStores } from 'src/components/Search/utils/mapStores';
import { mapMakes } from 'src/components/Search/utils/mapMakes';
import { mapVehicles } from 'src/components/Search/utils/mapVehicles';
import { mapAgents } from 'src/components/Search/utils/mapAgents';
import { MultiSelectFilter } from 'src/components/Search/components';
import { TextFieldFilter } from 'src/components/Search/components';
import { SelectFilter } from 'src/components/Search/components';
import { DateFilter } from 'src/components/Search/components';
import { TICKET_DATE_RANGE_FIELDS } from 'src/components/Search/constants';
import type { TDateRangeField } from 'src/components/Lead/Filters/components/types';
import type { TRangeValue } from 'src/components/Search/components/types';
import {
    ADMIN_LEVEL_ACCESS,
    DEVELOPER_LEVEL_ACCESS,
    GROUP_LEVEL_ACCESS,
    USER_LEVEL_ACCESS,
} from 'src/components/access';
import { IServiceClientFilterValues, TSelectItem } from './types';
import { SERVICE_LEAD_TYPE } from 'src/constants/lead/leadTypes';

const defaultFilters: Partial<IServiceClientFilterValues> = {
    store: [],
    make: [],
    name: '',
    email: '',
    phone: '',
    vehicle: [],
    assigned: [],
    assignment: '',
    createdAt: null,
};

interface ServiceClientAdvancedFiltersProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onApply: (filters: IServiceClientFilterValues) => void;
    values: IServiceClientFilterValues;
}

export const ServiceClientAdvancedFilters = ({
    open,
    setOpen,
    onApply,
    values,
}: ServiceClientAdvancedFiltersProps) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { stores, getStores } = useStore();
    const { makes, getMakes } = useMake();
    const {
        vehicles,
        getVehiclesAdvancedResults,
        clearState: clearVehicles,
    } = useVehicle();
    const { agents, getAgents, clearUserState } = useUser();

    const [filters, setFilters] = useState<Partial<IServiceClientFilterValues>>(
        defaultFilters
    );
    const [currentAgents, setCurrentAgents] = useState<TSelectItem[]>([]);
    const [currentDateRange, setCurrentDateRange] = useState<TDateRangeField>('createdAt');

    const getStoreOptions = (): TSelectItem[] => {
        if (!user?.accessLevel) return [];
        if (user.accessLevel === DEVELOPER_LEVEL_ACCESS) return mapStores(stores);
        if (
            user.accessLevel === ADMIN_LEVEL_ACCESS ||
            user.accessLevel === USER_LEVEL_ACCESS
        )
            return mapStores(user?.stores || []);
        if (user.accessLevel === GROUP_LEVEL_ACCESS)
            return mapStores(user?.group?.stores || []);
        return [];
    };

    const currentStores = getStoreOptions();
    const currentMakes = mapMakes(makes || []);
    const currentVehicles = mapVehicles(vehicles || []);

    const handleGetVehicles = (selectedMakes: TSelectItem[] | null) => {
        if (!selectedMakes || selectedMakes.length === 0) {
            clearVehicles();
            return;
        }
        getVehiclesAdvancedResults({
            filters: {
                make: { $in: selectedMakes.map((m) => m.value) },
            },
            select: 'make model',
            populate: 'make',
            sort: 'make model',
        });
    };

    useEffect(() => {
        if (user?.accessLevel === DEVELOPER_LEVEL_ACCESS) getStores();
    }, [user?.accessLevel]);

    useEffect(() => {
        if (open) {
            getMakes({ select: 'name' });
        }
    }, [open]);

    useEffect(() => {
        setCurrentAgents(mapAgents(agents || []));
    }, [agents]);

    useEffect(() => {
        const storeIds = (filters.store || []);
        if (storeIds.length > 0) {
            getAgents({
                stores: storeIds.map((s) => s.value),
                areas: SERVICE_LEAD_TYPE
            });
        } else {
            clearUserState();
        }
    }, [filters.store]);

    useEffect(() => {
        if (values && open) {
            setFilters(values);
            if (values.make && values.make.length > 0) {
                handleGetVehicles(values.make);
            } else {
                clearVehicles();
            }
        }
    }, [values, open]);

    const handleChangeMake = (selected: TSelectItem[] | unknown) => {
        const selectedArr = Array.isArray(selected) ? [...selected] : [];
        if (selectedArr.length > 0) {
            handleGetVehicles(selectedArr);
        } else {
            clearVehicles();
        }
        setFilters((prev) => ({ ...prev, make: selectedArr, vehicle: [] }));
    };

    const handleSelectChange = (field: 'store' | 'vehicle' | 'assigned') => (selected: TSelectItem[] | unknown) => {
        setFilters((prev) => ({ ...prev, [field]: Array.isArray(selected) ? [...selected] : [] }));
    };

    const handleDateRangeChange =
        (field: TDateRangeField) => (range: TRangeValue) => {
            if (field === 'createdAt') {
                setFilters((prev) => ({ ...prev, createdAt: range }));
            }
        };

    const handleClearDateRange = (field: TDateRangeField) => () =>
        field === 'createdAt' && setFilters((prev) => ({ ...prev, createdAt: null }));

    const handleInputChange = (field: 'name' | 'email' | 'phone' | 'assignment') => (e: { target: { value: string } }) => {
        setFilters((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));
    };

    const handleApply = () => {
        setOpen(false);
        onApply({
            ...values,
            ...filters,
            isSearching: true,
            page: 0,
        });
    };

    const handleClear = () => {
        setOpen(false);
        onApply({
            ...values,
            ...defaultFilters,
            isSearching: false,
        });
    };

    return (
        <EnchancedModal
            open={open}
            setOpen={setOpen}
            title={t('Buttons.AdvancedSearch')}
            maxWidth="md"
        >
            <CardContent
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 15,
                }}
            >
                <Typography>{String(t('ModalFilters.filters'))}</Typography>

                <Grid container spacing={2}>
                    <GridItem>
                        <MultiSelectFilter
                            label={String(t('ModalFilters.store'))}
                            options={currentStores}
                            value={filters.store || []}
                            onChange={handleSelectChange('store')}
                            placeholder={String(t('ModalFilters.placeholder.store'))}
                        />
                    </GridItem>
                    <GridItem>
                        <MultiSelectFilter
                            label={String(t('ModalFilters.make'))}
                            options={currentMakes}
                            value={filters.make || []}
                            onChange={handleChangeMake}
                            placeholder={String(t('ModalFilters.placeholder.make'))}
                        />
                    </GridItem>
                    <GridItem>
                        <TextFieldFilter
                            label={String(t('ModalFilters.placeholder.name'))}
                            value={filters.name || ''}
                            onChange={handleInputChange('name')}
                            placeholder={String(t('ModalFilters.placeholder.name'))}
                        />
                    </GridItem>
                    <GridItem>
                        <TextFieldFilter
                            label={String(t('ModalFilters.placeholder.email'))}
                            value={filters.email || ''}
                            onChange={handleInputChange('email')}
                            placeholder={String(t('ModalFilters.placeholder.email'))}
                        />
                    </GridItem>
                    <GridItem>
                        <TextFieldFilter
                            label={String(t('ModalFilters.placeholder.phone'))}
                            value={filters.phone || ''}
                            onChange={handleInputChange('phone')}
                            placeholder={String(t('ModalFilters.placeholder.phone'))}
                        />
                    </GridItem>
                    <GridItem>
                        <MultiSelectFilter
                            label={String(t('ModalFilters.vehicle'))}
                            options={currentVehicles}
                            value={filters.vehicle || []}
                            onChange={handleSelectChange('vehicle')}
                            placeholder={String(t('ModalFilters.placeholder.model'))}
                        />
                    </GridItem>
                    <GridItem>
                        <MultiSelectFilter
                            label={String(t('ModalFilters.agent'))}
                            options={currentAgents}
                            value={filters.assigned || []}
                            onChange={handleSelectChange('assigned')}
                            placeholder={String(t('ModalFilters.placeholder.agent'))}
                        />
                    </GridItem>
                    {user?.accessLevel !== USER_LEVEL_ACCESS && (
                        <GridItem>
                            <SelectFilter
                                label={String(t('ModalFilters.assignment'))}
                                value={filters.assignment ?? ''}
                                onChange={handleInputChange('assignment')}
                                placeholder={String(t('ModalFilters.placeholder.assignment'))}
                                options={[
                                    { label: t('AdvancedAll.Unassigned'), value: '' },
                                    { label: t('Leads.Assigned'), value: 'true' },
                                    { label: t('Leads.Unassigned'), value: 'false' },
                                ]}
                            />
                        </GridItem>
                    )}
                </Grid>

                <Divider />

                <Typography>{String(t('ModalFilters.dateRanges'))}</Typography>

                <Grid container spacing={2}>
                    {TICKET_DATE_RANGE_FIELDS.map((field) => (
                        <GridItem key={field}>
                            <DateFilter
                                field={field}
                                value={filters.createdAt ?? undefined}
                                setCurrentDateRange={setCurrentDateRange}
                                onClear={handleClearDateRange(field)}
                                isActive={currentDateRange === field}
                                handleDateRangeChange={handleDateRangeChange}
                            />
                        </GridItem>
                    ))}
                </Grid>

                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '1em',
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClear}
                        style={{
                            minHeight: 55,
                            minWidth: 250,
                        }}
                    >
                        {String(t('ModalFilters.clear'))}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApply}
                        style={{
                            minHeight: 55,
                            minWidth: 250,
                        }}
                    >
                        {String(t('ModalFilters.apply'))}
                    </Button>
                </Box>
            </CardContent>
        </EnchancedModal>
    );
};
